import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import api from '../../../api';
import { useNavigate } from 'react-router-dom';

export default function Candidates() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    shortlisted: 0,
    qualified: 0,
    underReview: 0,
  });

  useEffect(() => {
    loadCandidates();
    loadJobs();
  }, [selectedJob, selectedStatus]);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      const params = {
        // Focus on qualified candidates only (not pending or rejected)
        status: selectedStatus || 'shortlisted,qualified,under_review',
      };
      if (selectedJob) params.job_id = selectedJob;

      const response = await api.get('/public-applications', { params });
      const data = response.data.data || [];
      setCandidates(data);

      // Calculate stats
      setStats({
        total: data.length,
        shortlisted: data.filter(c => c.status === 'shortlisted').length,
        qualified: data.filter(c => c.status === 'qualified').length,
        underReview: data.filter(c => c.status === 'under_review').length,
      });
    } catch (error) {
      console.error('Error loading candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await api.get('/public-applications/jobs');
      const jobsData = response.data.jobs || response.data || [];
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      under_review: '#2196F3',
      shortlisted: '#4CAF50',
      qualified: '#8BC34A',
    };
    return colors[status] || '#757575';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const filteredCandidates = candidates.filter((candidate) =>
    candidate.candidate?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    candidate.candidate?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    candidate.candidate?.mobile?.includes(searchTerm)
  );

  const handleViewDetails = (candidateId) => {
    navigate('/applications', { state: { selectedApplicationId: candidateId } });
  };

  const handleDownloadCV = async (candidate) => {
    if (candidate.cv_url) {
      try {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = candidate.cv_url;
        link.download = `CV_${candidate.candidate?.full_name || 'candidate'}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading CV:', error);
        // Fallback to opening in new tab if download fails
        window.open(candidate.cv_url, '_blank');
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Qualified Candidates"
        subtitle="View and manage shortlisted and qualified candidates"
        icon={StarIcon}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              bgcolor: '#1a237e',
              color: 'white',
              boxShadow: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Candidates
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              bgcolor: '#37474f',
              color: 'white',
              boxShadow: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.underReview}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Under Review
                </Typography>
              </Box>
              <AssessmentIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              bgcolor: '#004d40',
              color: 'white',
              boxShadow: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.shortlisted}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Shortlisted
                </Typography>
              </Box>
              <StarIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 3,
              bgcolor: '#4a148c',
              color: 'white',
              boxShadow: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.qualified}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Qualified
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300, flexGrow: 1 }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 250 }} size="small">
            <InputLabel>Filter by Job</InputLabel>
            <Select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              label="Filter by Job"
            >
              <MenuItem value="">All Jobs</MenuItem>
              {jobs.map((job) => (
                <MenuItem key={job.job_id} value={job.job_id}>
                  {job.job_title || job.posting_title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="">All Qualified</MenuItem>
              <MenuItem value="under_review">Under Review</MenuItem>
              <MenuItem value="shortlisted">Shortlisted</MenuItem>
              <MenuItem value="qualified">Qualified</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Candidates Grid */}
      {!loading && (
        <Grid container spacing={3}>
          {filteredCandidates.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <StarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No qualified candidates found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Candidates with status "Shortlisted", "Qualified", or "Under Review" will appear here
                </Typography>
              </Paper>
            </Grid>
          ) : (
            filteredCandidates.map((candidate) => (
              <Grid item xs={12} sm={6} md={4} key={candidate.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header with Avatar and Status */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        src={candidate.candidate?.profile_picture_url || candidate.profile_picture_url}
                        sx={{
                          width: 64,
                          height: 64,
                          mr: 2,
                          bgcolor: getStatusColor(candidate.status),
                        }}
                      >
                        {candidate.candidate?.full_name?.charAt(0) || 'N'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {candidate.candidate?.full_name || 'N/A'}
                        </Typography>
                        <Chip
                          label={formatStatus(candidate.status)}
                          size="small"
                          icon={<StarIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            bgcolor: `${getStatusColor(candidate.status)}15`,
                            color: getStatusColor(candidate.status),
                            fontWeight: 600,
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Job Applied For */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <WorkIcon sx={{ fontSize: 18, mr: 1, color: '#29204C' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {candidate.job?.job_title || candidate.job_title}
                      </Typography>
                    </Box>

                    {/* Contact Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {candidate.candidate?.email || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {candidate.candidate?.mobile || candidate.mobile || 'N/A'}
                      </Typography>
                    </Box>

                    {candidate.candidate?.city && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {candidate.candidate?.city}{candidate.candidate?.nationality ? `, ${candidate.candidate.nationality}` : ''}
                        </Typography>
                      </Box>
                    )}

                    {/* Applied Date */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      Applied on {new Date(candidate.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <Divider />
                  <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<AssignmentIcon />}
                      onClick={() => handleViewDetails(candidate.id)}
                      sx={{
                        flexGrow: 1,
                        bgcolor: '#29204C',
                        color: 'white',
                        '&:hover': { bgcolor: '#1a0f2e' },
                      }}
                    >
                      View Details
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadCV(candidate)}
                      sx={{
                        border: '1px solid #29204C',
                        color: '#29204C',
                        '&:hover': { bgcolor: '#29204C10' },
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}
    </Container>
  );
}
